// import moment from 'moment'
import { NextPage } from 'next'
import Image from 'next/image'
import { useEffect, useState } from 'react'
import { TailSpin } from 'react-loading-icons'
import { useSelector } from 'react-redux'
import { WriterSection } from 'src/components/Author/WriterSection'
import { ContentWritersByline } from 'src/components/ContentWritersByline/ContentWritersByline'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import { MaterialSection } from 'src/components/Material/MaterialSection'
import { AMPLIFY_USER_POOL_APP_CLIENT_ID } from 'src/config/config'
import { IContentEntityBySlug, useContentBySlugQuery, useVisitContentMutation } from 'src/graphql/generated/hooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { useDefaultLogo } from 'src/hooks/useDefaultLogo'
import { AppState } from 'src/store/types'
import { formatNumericDisplayDate } from 'src/utils/formatNumericDisplayDate'
import { normalizeWritersList } from 'src/utils/normalizeContentWriters'

// import { content } from 'tailwindcss/defaultTheme'
// import { ArticleCart } from '../ArticleCart/ArticleCart'
import { PremiumModal } from '../global/PremiumModal'
// import { LegaltechJobsPanel } from '../LegaltechJobsPanel/LegaltechJobsPanel'
import FreeLimitPopup from '../Popup/FreeLimitPopup'
import { RequestVendorCheatsheetButton } from '../RequestVendorCheatsheetButton/RequestVendorCheatsheetButton'

type Props = {
  slug: string
  contentSnippet?: string
  content?: IContentEntityBySlug
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const ContentPageView: NextPage<Props> = ({ slug, contentSnippet = '', content = null }) => {
  const { data, loading } = useContentBySlugQuery({
    fetchPolicy: 'cache-and-network',
    variables: { slug },
  })
  const title = data?.contentBySlug?.content?.attributes?.title || ''
  const writersList = normalizeWritersList(data?.contentBySlug?.content?.attributes?.writer)
  const snippet = data?.contentBySlug?.content?.attributes?.snippet ?? ''
  const defaultImageUrl =
    data?.contentBySlug?.content?.attributes?.defaultImage &&
    data?.contentBySlug?.content?.attributes?.defaultImage.length > 0
      ? data?.contentBySlug?.content?.attributes?.defaultImage[0]?.url
      : null
  const imageUrl = useDefaultLogo(defaultImageUrl)
  const showImage = !!defaultImageUrl
  const publishedTime = formatNumericDisplayDate(data?.contentBySlug?.content?.attributes?.publishedTime)

  const isContentPremium = !!data?.contentBySlug?.content?.attributes?.isPremium
  // const fullContent = data?.contentBySlug || null
  // const downloadMaterial = fullContent?.content?.attributes?.downloadMaterial
  // const fullContentSnippet = fullContent?.content?.attributes?.snippet || ''
  // const isInvited = fullContent?.isInvited || false

  const authInfo = useSelector((state: AppState) => state.authInfo)
  const [visitContent] = useVisitContentMutation()
  const [isInvited, setIsInvited] = useState<boolean>(false)
  const [isFreeArticle, setIsFreeArticle] = useState<boolean | null>(null)
  const [showModal, setShowModal] = useState(false)
  const [isNonLogin, setIsNonLogin] = useState(false)
  const [lastFreeArticle, setLastFreeArticle] = useState(false)
  // const [includeInKnowHow, setIncludeInKnowHow] = useState<boolean | null>(false)
  const [counted, setCounted] = useState<boolean>(false)

  let { isPremium, isVendor } = useAuthenticated()

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (localStorage.getItem(`sso`)) {
        const ssoAuthUser = localStorage.getItem(`sso`)
        setIsNonLogin(!ssoAuthUser)
      } else {
        const lastAuthUser = localStorage.getItem(
          `CognitoIdentityServiceProvider.${AMPLIFY_USER_POOL_APP_CLIENT_ID}.LastAuthUser`,
        )
        setIsNonLogin(!lastAuthUser)
      }
    }
  }, [])

  useEffect(() => {
    if (data?.contentBySlug?.isFreeArticle !== null) {
      setIsFreeArticle(data?.contentBySlug?.isFreeArticle ?? false)
    }
    if (data?.contentBySlug) {
      let invited = false
      let freeArticle = null
      if (data?.contentBySlug?.isFreeArticle == false) {
        freeArticle = false
      } else {
        freeArticle = data?.contentBySlug?.isFreeArticle || null
      }
      if (data?.contentBySlug?.isFreeArticle && data?.contentBySlug?.freeArticleLeft == 0) {
        setLastFreeArticle(true)
      }
      const vendorSelfContent = data?.contentBySlug?.vendorSelfContent || false
      setIsFreeArticle(freeArticle)
      // setIncludeInKnowHow(data?.contentBySlug?.includeInKnowHow || false)
      isPremium = !!authInfo.isPremium
      isVendor = !!authInfo.isVendor
      const resourcePublishedAt = data?.contentBySlug?.resourcePublishedAt

      if (resourcePublishedAt) {
        const yearAgoDate = new Date()
        const publishDate = new Date(resourcePublishedAt)

        // get 1 year ago date from the current date
        yearAgoDate.setFullYear(yearAgoDate.getFullYear() - 1)
        invited = (data?.contentBySlug?.isInvited && publishDate >= yearAgoDate) || false
      }
      setIsInvited(invited)
      // const premiumContent =
      //   isPremium || freeArticle || !isContentPremium || invited || vendorSelfContent || isLatestBlog || false
      // console.log(premiumContent)
      let premiumContent = false
      if (freeArticle) {
        premiumContent = true //show all content if it free limit is available
      } else {
        if (isPremium) {
          premiumContent = isPremium && (!isVendorCheatsheet || isVendor) //show all content if user is premium but show the vendor content only to premium vendor
        } else {
          //for basic user
          if (!isContentPremium) {
            premiumContent =
              ((invited || vendorSelfContent || isLatestBlog) && (!isVendorCheatsheet || isVendor)) || false
          } else {
            premiumContent =
              ((!isContentPremium || invited || vendorSelfContent || isLatestBlog) &&
                (!isVendorCheatsheet || isVendor)) ||
              false
          }
        }
      }
      setIsShowingPremiumContent(premiumContent)
      setFullContent(data?.contentBySlug as IContentEntityBySlug)
      setVendorSelfContent(vendorSelfContent)
    }
  }, [data])

  const isLatestBlog = data?.contentBySlug?.content?.attributes?.isLatestBlog ?? false
  const isVendorCheatsheet = data?.contentBySlug?.content?.attributes?.blogTaxonomies?.some(
    (item) => item?.slug === 'the-vendor-cheatsheet',
  )
  const isShowingVendorData = !isVendorCheatsheet || isVendor || isLatestBlog
  const [isShowingPremiumContent, setIsShowingPremiumContent] = useState<boolean>(
    ((!isContentPremium || isLatestBlog || isPremium || isInvited) && isShowingVendorData) || false,
  )
  const [fullcontent, setFullContent] = useState<IContentEntityBySlug>(
    (data?.contentBySlug as IContentEntityBySlug) || content,
  )

  // Prefer pre-fetched teaser snippet from parent container when available.
  const displaySnippet = contentSnippet?.trim() ? contentSnippet : snippet
  const displayContent = fullcontent?.content?.attributes?.snippet || ''
  const displayTitle = title
  const [vendorSelfContent, setVendorSelfContent] = useState<boolean>(false)

  const countData = async (cancelExcution = false) => {
    if (counted || cancelExcution) {
      return
    }

    await visitContent({ variables: { content_id: data?.contentBySlug?.content?.id || '' } })
  }
  useEffect(() => {
    if (
      authInfo.idToken &&
      authInfo.id &&
      !counted &&
      data?.contentBySlug?.vendorSelfContent == false &&
      data?.contentBySlug?.isFreeArticle == null
    ) {
      countData()
      setCounted(true)
    }
  }, [fullcontent])

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const handleOpenModal = () => {
    setShowModal(true)
  }

  const freeArticleWithpremiumButton = (
    <FreeLimitPopup
      heading={``}
      title={
        <>
          Signup for <span className="text-navybright">premium</span> to access this article.
        </>
      }
      createAccountButton={false}
      premiumSignupButton={true}
    />
  )

  const freeArticleWithpremiumButtonForAllUser = (
    <FreeLimitPopup
      heading={`You have reached your free article limit for the month.`}
      title={
        <>
          Sign up for an LTH <span className="text-navybright">Premium</span> subscription to access unlimited articles.
        </>
      }
      createAccountButton={false}
      premiumSignupButton={true}
    />
  )

  const lastFreeArticleWithpremiumButton = (
    <>
      <FreeLimitPopup
        heading={`This is your last free article this month.`}
        title={
          <>
            To access unlimited articles, sign up for LTH <span className="text-navybright">Premium</span>.
          </>
        }
        createAccountButton={false}
        premiumSignupButton={true}
        cssModified={true}
      />
    </>
  )

  if (loading) {
    return (
      <div className="w-full flex items-center justify-center my-[40px]">
        <TailSpin stroke="#011D58" height="2em" /> <div className="pl-3"> Loading...</div>
      </div>
    )
  }

  return (
    <div className="w-full items-center justify-between">
      <PremiumModal origin={slug} isOpen={showModal} onClose={handleCloseModal} />
      <div className="">
        <div className="w-full items-center justify-between">
          <div
            className={`!max-w-[1100px] flex flex-col w-full m-auto bg-white  ${!isFreeArticle && isFreeArticle != null ? 'box-border-shadow-closing' : ''}`}
          >
            <div className="w-full h-[0.4rem]"></div>
            <div
              className={`tdBody flex flex-col md:pl-[4.0625rem] md:pr-[4.0625rem] pl-[20px] pr-[20px] ${!isFreeArticle && isFreeArticle != null ? 'tdBody-bottom-closing' : ''}`}
            >
              <h1
                className="pt-[0.1rem] pb-[0.125rem] font-bold font-secondary text-darkgray text-[1.875rem] mt-[2rem]"
                dangerouslySetInnerHTML={{ __html: displayTitle }}
              />
              <div className="print-flex flex flex-row mb-[2.2rem] mt-[1rem] items-center flex-wrap">
                <span className="resource-desc mr-[0.425rem]" style={{ fontSize: '18px' }}>
                  Published on {publishedTime}
                </span>
                {writersList.length > 0 && (
                  <>
                    <span className="resource-desc mr-[0.425rem]" style={{ fontSize: '18px' }}>
                      &nbsp;by
                    </span>
                    <ContentWritersByline writers={writersList} variant="detail" />
                  </>
                )}
              </div>
              <div className="divide-x h-[1px] bg-mist mb-[1.6rem]"></div>
              {isShowingPremiumContent &&
                !['blog', 'industry_analysis', 'vendor_resource', 'thought_leadership', 'buyers_guide'].includes(
                  fullcontent?.content?.attributes?.contentType?.toLowerCase() || '',
                ) &&
                fullcontent?.content?.attributes?.downloadMaterial && (
                  <div className="blog-content html-show mb-[2rem] py-[1rem]">
                    {isShowingPremiumContent && (
                      <div className="materialSectionPrint md:float-right md:ml-[40px] mt-10 md:mt-0 md:mb-4 max-w-[500px] min-w-[465px] max-h-[500px] min-h-[496px]">
                        <MaterialSection
                          isInvited={isInvited}
                          contentId={fullcontent?.content?.attributes?.documentId || ''}
                          downloadMaterial={fullcontent.content.attributes?.downloadMaterial}
                          isContentPremium={isContentPremium}
                          onDisabledDownloadClick={handleOpenModal}
                          vendorSelfContent={vendorSelfContent}
                          isFreeArticle={isFreeArticle}
                        />
                      </div>
                    )}
                    <div className="html-show">
                      <HtmlContentViewer
                        className="blog-content html-show font-secondary font-semibold text-darkgray text-[22px] leading-[32px]"
                        html={displaySnippet}
                      />
                    </div>
                  </div>
                )}
              {isShowingPremiumContent && (
                <HtmlContentViewer
                  className="blog-content html-show mb-[3rem] w-full overflow-auto"
                  html={displayContent}
                  image={
                    !fullcontent?.content?.attributes?.downloadMaterial && imageUrl && showImage ? (
                      <Image
                        className="w-full bg-gray object-contain	"
                        alt="logo"
                        src={imageUrl}
                        width={1000}
                        height={1000}
                        priority
                      />
                    ) : null
                  }
                  download={
                    fullcontent?.content?.attributes?.downloadMaterial &&
                    ![
                      'business_case',
                      'competitive_analysis',
                      'evaluation_framework',
                      'knowhow',
                      'landscape_article',
                      'legaltech_resources_for_lawyers',
                      'pilot_guide',
                      'premium_resource',
                      'requirements_template',
                      'vendor_analysis',
                      'vendor_content',
                    ].includes(fullcontent?.content?.attributes?.contentType?.toLowerCase() || '') ? (
                      <div className="mb-10">
                        <MaterialSection
                          isInvited={isInvited}
                          contentId={fullcontent?.content?.attributes?.documentId || ''}
                          downloadMaterial={fullcontent.content.attributes?.downloadMaterial}
                          isContentPremium={isContentPremium}
                          onDisabledDownloadClick={handleOpenModal}
                          vendorSelfContent={vendorSelfContent}
                          isFreeArticle={isFreeArticle}
                        />
                      </div>
                    ) : null
                  }
                />
              )}
              {!isShowingPremiumContent && (
                <HtmlContentViewer
                  className="blog-content html-show mb-[3rem] w-full overflow-auto"
                  html={displayContent}
                  image={
                    imageUrl && showImage ? (
                      <Image
                        className="w-full bg-gray object-contain	"
                        alt="logo"
                        src={imageUrl}
                        width={1000}
                        height={1000}
                        priority
                      />
                    ) : null
                  }
                />
              )}
              {/* this is for basic user/premium user and includeinknowhow is false */}
              {!isShowingPremiumContent && !isNonLogin && (
                <div className="flex justify-center">
                  {/* this is for premium user only */}
                  {isVendorCheatsheet && isPremium && <RequestVendorCheatsheetButton />}
                  {/* this is for basic user and includeinknowhow is false */}
                  {!isPremium && isFreeArticle == null && freeArticleWithpremiumButton}
                </div>
              )}
              {/* this is for not login user and includeinknowhow is false */}
              {isNonLogin && isFreeArticle == null && freeArticleWithpremiumButton}
              {/* this is for not login user/basic user and includeinknowhow is true */}
              {!isFreeArticle && isFreeArticle != null && freeArticleWithpremiumButtonForAllUser}
              {/* this is for not login/basic user and includeinknowhow is true and last free article is true */}
              {isFreeArticle && lastFreeArticle && lastFreeArticleWithpremiumButton}

              {/* {(isFreeArticle === true || (isPremium && includeInKnowHow === false)) &&
                relatedContentsArray &&
                relatedContentsArray.length > 0 && <ArticleCart articles={relatedContentsArray} from="contents" />} */}
              {isFreeArticle && writersList.map((w, i) => <WriterSection key={i} writer={w} />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
